import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useLocation } from "react-router-dom";
import { sensorNameList, statusList } from "../../utils/SensorNameMappings.js";
import smartParkService from "../../axios";
import "../../assets/scss/App.scss";
import styles from "../../components/components.js";
import ClipLoader from "react-spinners/ClipLoader";
import { css } from "@emotion/core";
import _ from "lodash";
import { login_, token_ } from "../../reducers/login";
import { checkPermissions } from "../../utils/checkPermissions.js";
import { Paper, Tab, Tabs, Box, Chip, Grid } from "@material-ui/core";
import { paperAutoAdjustIntervalInMS } from "../../config/index";
import { FilterChips } from "./Components/FilterChips.jsx";
import { SensorConfigGrid } from "./Components/SensorConfigGrid.jsx";

const override = css`
  display: block;
  margin: 250px auto;
  border-color: #0076bb;
`;

const useStyles = makeStyles(styles);

const getInitialTabFromUrl = (search) => {
  const tabParam = new URLSearchParams(search).get("selected");
  const tabIndex = parseInt(tabParam, 10);
  return !isNaN(tabIndex) && tabIndex >= 0 ? tabIndex : 0;
};

const getInitialFilterFromUrl = (search) => {
  return new URLSearchParams(search).get("filter") || "all";
};

export default function ConfigList(props) {
  const location = useLocation();
  const history = useHistory();
  const [selectedFacility, setSelectedFacility] = useState(null);
  const params = new URLSearchParams(location.search);
  const facility = params.get("facility");
  const loginDetails = useSelector(login_);
  const userid = _.get(loginDetails, ["UserID"], "");
  const token = useSelector(token_);
  smartParkService.defaults.headers.common["x-access-token"] = token;
  const [loading, setLoading] = React.useState(true);
  const [refresh, setRefresh] = React.useState(false);
  const [color, setColor] = React.useState("#ffffff");
  const [tabsArray, setTabsArray] = useState([]);
  const [selectedTab, setSelectedTab] = useState(() =>
    getInitialTabFromUrl(location.search),
  );
  const [tableData, setTableData] = useState([]);
  const [sensorData, setSensorData] = useState({});
  const [selectedFilter, setSelectedFilter] = React.useState(() =>
    getInitialFilterFromUrl(location.search),
  );
  const [filterArray, setFilterArray] = useState([]);
  const classes = useStyles();

  const updateUrlParams = ({ tab, filter }) => {
    const nextParams = new URLSearchParams(location.search);

    if (tab !== undefined) {
      nextParams.set("selected", String(tab));
    }

    if (filter !== undefined) {
      nextParams.set("filter", filter);
    }

    const nextSearch = nextParams.toString();
    const currentSearch = location.search.replace(/^\?/, "");

    if (nextSearch !== currentSearch) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch,
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    updateUrlParams({ tab: newValue });
  };

  const handleFilterChange = (option) => {
    setSelectedFilter(option.value);
    updateUrlParams({ filter: option.value });
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const data = await smartParkService
        .post(
          "/api/configuration/sensor/options",
          { FacilityIDs: [facility] },
          {
            headers: {
              userid,
            },
          },
        )
        .then((response) => {
          if (!response) return;

          const { Sensors } = response.data;
          const facilityName =
            response?.data?.Metadata?.Facilities[0]?.park_name;
          setSelectedFacility(facilityName);
          const processedData = {};
          const list = Object.entries(Sensors).map(([key, value], idx) => {
            if (!processedData[key]) {
              processedData[key] = { all: [] };
            }

            let totalSensors = 0;
            Object.entries(value).forEach(([k, v]) => {
              processedData[key][k] = v;
              processedData[key].all.push(...v);
              totalSensors += Array.isArray(v) ? v.length : 0;
            });

            return {
              name: key,
              label: sensorNameList[key] ?? key,
              value: idx,
              count: totalSensors,
              status: Object.keys(processedData[key]),
            };
          });

          setTabsArray(list);
          return processedData;
        });

      setSensorData(data || {});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setSelectedFacility(location?.state?.details?.header);
  }, []);

  useEffect(() => {
    fetchData();
  }, [facility, refresh]);

  useEffect(() => {
    const urlTab = getInitialTabFromUrl(location.search);
    const urlFilter = getInitialFilterFromUrl(location.search);

    if (urlTab !== selectedTab) {
      setSelectedTab(urlTab);
    }

    if (urlFilter !== selectedFilter) {
      setSelectedFilter(urlFilter);
    }
  }, [location.search]);

  useEffect(() => {
    if (Object.entries(sensorData).length < 1 || tabsArray.length === 0) return;

    const selectedTabItem = tabsArray.find(
      (sensor) => sensor.value === selectedTab,
    );

    if (!selectedTabItem) {
      setSelectedTab(0);
      updateUrlParams({ tab: 0 });
      return;
    }

    const selectedSensor = selectedTabItem.name;
    const sensorFilters = sensorData[selectedSensor];
    if (!sensorFilters) return;

    const status = Object.entries(sensorFilters).map(([key, value]) => {
      return {
        name: key,
        label: statusList[key]?.label ?? key,
        color: statusList[key]?.color ?? "default",
        value: key,
        count: value.length,
      };
    });
    setFilterArray(status);

    const activeFilter = sensorFilters[selectedFilter] ? selectedFilter : "all";
    if (activeFilter !== selectedFilter) {
      setSelectedFilter(activeFilter);
      updateUrlParams({ filter: activeFilter });
    }

    setTableData(sensorFilters[activeFilter] || []);
  }, [selectedTab, sensorData, selectedFilter, tabsArray]);

  setTimeout(() => {
    const element = document.getElementsByClassName("configGrid__SmartPark");
    if (element && element.length > 0) {
      const calculatedHeight = element[0].clientHeight + 25 + "px";
      const paperElement = document.getElementById("configPaper__SmartPark");
      if (paperElement) {
        paperElement.style.height = calculatedHeight;
      }
    }
  }, paperAutoAdjustIntervalInMS);

  return (
    <div className={classes.root}>
      {checkPermissions({
        permission: "Configuration",
        permissionType: "read",
      }) ? (
        <div className={classes.contentPage}>
          <section class="content-header">
            <h3 class="float-left">Sensor Configuration</h3>
            <div class="float-right">
              <ol class="breadcrumb">
                <li class="breadcrumb-item">
                  <a href="/overview">Home</a>
                </li>
                <li class="breadcrumb-item">
                  <a class="active" href="#">
                    Sensor Configuration
                  </a>
                </li>
              </ol>
            </div>
          </section>
          <section class="content">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper className={classes.paper}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Tabs
                        value={selectedTab}
                        textColor="secondary"
                        indicatorColor="secondary"
                        aria-label="Sensor type tabs"
                        onChange={handleTabChange}
                      >
                        {tabsArray.map((item, idx) => {
                          return (
                            <Tab
                              key={item.label}
                              value={item.value}
                              label={
                                <Box
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                  }}
                                >
                                  {item.label}
                                  {item.count >= 0 && (
                                    <Chip
                                      label={item.count}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      style={{
                                        backgroundColor:
                                          selectedTab === idx
                                            ? "white"
                                            : "transparent",
                                        color:
                                          selectedTab === idx
                                            ? "#3f51b5"
                                            : "inherit",
                                        borderColor:
                                          selectedTab === idx
                                            ? "white"
                                            : undefined,
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                            />
                          );
                        })}
                      </Tabs>
                    </Grid>
                    <Grid item xs={12}>
                      <FilterChips
                        options={filterArray}
                        selectedValue={selectedFilter}
                        clickHandler={handleFilterChange}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <ClipLoader
                      color={color}
                      loading={loading}
                      css={override}
                      size={40}
                    />
                  </Box>
                ) : (
                  <SensorConfigGrid
                    rows={tableData}
                    refreshHandler={() => {
                      setRefresh((r) => !r);
                    }}
                    selectedFacility={selectedFacility}
                  />
                )}
              </Grid>
            </Grid>
          </section>
        </div>
      ) : loading ? (
        <ClipLoader color={color} loading={loading} css={override} size={60} />
      ) : (
        <div
          style={{
            color: "red",
            position: "absolute",
            top: "50%",
            left: "40%",
            fontSize: "20px",
            transform: "translate(-50%, -50%)",
          }}
        >
          You do not have read permissions. Please contact Admin
        </div>
      )}
    </div>
  );
}
